import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  Shield,
  Search,
  Filter,
  User,
  FileText,
  RefreshCw,
  Download,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Info,
  XCircle,
  Clock,
  Tag,
  Eye,
  X,
  BarChart3
} from 'lucide-react';
import { notify } from '@core/notifications/notify';
import PageInfoButton from '@/components/PageInfoButton';

interface AuditLog {
  id: number;
  actor_id: number;
  actor_name: string;
  actor_email: string;
  action: string;
  entity_type: string;
  entity_id: number;
  entity_name: string;
  details: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
}

const AuditLogs: React.FC = () => {
  const { hasPermission, isLoading: authLoading } = useAuth();
  const canViewAuditLogs = hasPermission('audit_logs.view');
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [sortField, setSortField] = useState<'created_at' | 'action' | 'actor_name'>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadLogs();
  }, [pagination.page, pagination.limit]);

  // Analytics calculations
  const analytics = useMemo(() => {
    const totalLogs = logs.length;

    // Action distribution
    const actionCounts = logs.reduce((acc, log) => {
      acc[log.action] = (acc[log.action] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topActions = Object.entries(actionCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);

    // Actor distribution
    const actorCounts = logs.reduce((acc, log) => {
      const actorKey = `${log.actor_name} (${log.actor_email})`;
      acc[actorKey] = (acc[actorKey] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topActors = Object.entries(actorCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);

    // Entity type distribution
    const entityTypeCounts = logs.reduce((acc, log) => {
      acc[log.entity_type] = (acc[log.entity_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topEntityTypes = Object.entries(entityTypeCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);

    // Activity over time (rough estimate)
    const recentActivity = logs.filter(log => {
      const logTime = new Date(log.created_at).getTime();
      const oneHourAgo = Date.now() - (60 * 60 * 1000);
      return logTime > oneHourAgo;
    }).length;

    return {
      totalLogs,
      topActions,
      topActors,
      topEntityTypes,
      recentActivity,
      avgLogsPerHour: totalLogs / 24 // Rough estimate
    };
  }, [logs]);

  const loadLogs = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });

      // Add filters if they exist
      if (filterAction) params.append('action', filterAction);
      if (filterEntityType) params.append('entity', filterEntityType);
      if (filterStartDate) params.append('startDate', filterStartDate);
      if (filterEndDate) params.append('endDate', filterEndDate);

      const response = await fetch(`/api/admin/audit-logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setLogs(result.data || []);
        setPagination(result.pagination || pagination);
      } else {
        notify.error('Failed to load audit logs');
      }
    } catch (error) {
      console.error('Error loading audit logs:', error);
      notify.error('Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = !searchTerm ||
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.actor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.entity_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAction = !filterAction || log.action === filterAction;
    const matchesEntityType = !filterEntityType || log.entity_type === filterEntityType;
    const matchesStartDate = !filterStartDate || new Date(log.created_at) >= new Date(filterStartDate);
    const matchesEndDate = !filterEndDate || new Date(log.created_at) <= new Date(filterEndDate);
    return matchesSearch && matchesAction && matchesEntityType && matchesStartDate && matchesEndDate;
  }).sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (field: 'created_at' | 'action' | 'actor_name') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getActionIcon = (action: string) => {
    const lowerAction = action.toLowerCase();
    if (lowerAction.includes('create') || lowerAction.includes('add')) return <CheckCircle size={16} className="text-emerald-600" />;
    if (lowerAction.includes('delete') || lowerAction.includes('remove')) return <XCircle size={16} className="text-red-600" />;
    if (lowerAction.includes('update') || lowerAction.includes('edit')) return <RefreshCw size={16} className="text-blue-600" />;
    if (lowerAction.includes('login') || lowerAction.includes('logout')) return <User size={16} className="text-purple-600" />;
    if (lowerAction.includes('error') || lowerAction.includes('fail')) return <AlertTriangle size={16} className="text-amber-600" />;
    return <Info size={16} className="text-slate-600" />;
  };

  const getActionColor = (action: string) => {
    const lowerAction = action.toLowerCase();
    if (lowerAction.includes('create') || lowerAction.includes('add')) return 'bg-emerald-100 text-emerald-700';
    if (lowerAction.includes('delete') || lowerAction.includes('remove')) return 'bg-red-100 text-red-700';
    if (lowerAction.includes('update') || lowerAction.includes('edit')) return 'bg-blue-100 text-blue-700';
    if (lowerAction.includes('login') || lowerAction.includes('logout')) return 'bg-purple-100 text-purple-700';
    if (lowerAction.includes('error') || lowerAction.includes('fail')) return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-700';
  };

  const getEntityTypeColor = (entityType: string) => {
    switch (entityType) {
      case 'user': return 'bg-blue-100 text-blue-700';
      case 'company': return 'bg-purple-100 text-purple-700';
      case 'framework': return 'bg-emerald-100 text-emerald-700';
      case 'template': return 'bg-amber-100 text-amber-700';
      case 'document': return 'bg-pink-100 text-pink-700';
      case 'role': return 'bg-indigo-100 text-indigo-700';
      case 'permission': return 'bg-cyan-100 text-cyan-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const handleExport = () => {
    const csv = [
      ['Timestamp', 'Actor', 'Action', 'Entity Type', 'Entity', 'Details', 'IP Address'].join(','),
      ...filteredLogs.map(log => [
        formatTimestamp(log.created_at),
        log.actor_name,
        log.action,
        log.entity_type,
        log.entity_name,
        `"${(typeof log.details === 'object'
          ? JSON.stringify(log.details)
          : String(log.details || '')).replace(/"/g, '""')}"`,
        log.ip_address
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!canViewAuditLogs) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="bg-red-600 px-6 py-5">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-white" size={24} />
              <div>
                <h1 className="text-lg font-bold text-white">Access Denied</h1>
                <p className="text-red-100 text-sm mt-0.5">Restricted Area</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <p className="text-slate-600 text-sm">Only Super Admin can access Audit Logs.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-500 text-sm">Loading audit logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Audit Logs</h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm mt-1">Track all system actions and changes</p>
          </div>
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors"
          >
            <Download size={16} />
            Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-3xl border border-slate-100 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Action</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <select
                  value={filterAction}
                  onChange={(e) => setFilterAction(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-colors appearance-none cursor-pointer"
                >
                  <option value="">All Actions</option>
                  <option value="create">Create</option>
                  <option value="update">Update</option>
                  <option value="delete">Delete</option>
                  <option value="login">Login</option>
                  <option value="logout">Logout</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Entity Type</label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <select
                  value={filterEntityType}
                  onChange={(e) => setFilterEntityType(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-colors appearance-none cursor-pointer"
                >
                  <option value="">All Entity Types</option>
                  <option value="user">User</option>
                  <option value="company">Company</option>
                  <option value="framework">Framework</option>
                  <option value="template">Template</option>
                  <option value="document">Document</option>
                  <option value="role">Role</option>
                  <option value="permission">Permission</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Start Date</label>
              <input
                type="datetime-local"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">End Date</label>
              <input
                type="datetime-local"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-colors"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterAction('');
                  setFilterEntityType('');
                  setFilterStartDate('');
                  setFilterEndDate('');
                }}
                className="w-full px-4 py-2.5 bg-slate-100 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Analytics Dashboard */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Activity Overview */}
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-900">Activity Overview</h3>
              <BarChart3 className="text-slate-400" size={18} />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Total Activities</span>
                <span className="text-sm font-bold text-blue-600">{analytics.totalLogs}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Recent Activity (1h)</span>
                <span className="text-sm font-bold text-green-600">{analytics.recentActivity}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Avg Activities/Hour</span>
                <span className="text-sm font-bold text-purple-600">{analytics.avgLogsPerHour.toFixed(1)}</span>
              </div>
            </div>
          </div>

          {/* Top Actions */}
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-900">Top Actions</h3>
              <FileText className="text-slate-400" size={18} />
            </div>
            <div className="space-y-3">
              {analytics.topActions.slice(0, 4).map(([action, count]: [string, number]) => (
                <div key={action} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getActionIcon(action)}
                    <span className="text-sm text-slate-900">{action}</span>
                  </div>
                  <span className="text-sm font-bold text-slate-600">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Actors */}
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-900">Most Active Users</h3>
              <User className="text-slate-400" size={18} />
            </div>
            <div className="space-y-3">
              {analytics.topActors.slice(0, 4).map(([actor, count]: [string, number]) => (
                <div key={actor} className="flex items-center justify-between">
                  <span className="text-sm text-slate-900 truncate max-w-32" title={actor}>{actor.split(' ')[0]}</span>
                  <span className="text-sm font-bold text-slate-600">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="text-slate-400" size={18} />
                <h3 className="text-sm font-semibold text-slate-900">Audit Logs</h3>
              </div>
              <span className="px-2 py-1 rounded-md font-bold text-[10px] bg-blue-100 text-blue-700">
                {filteredLogs.length}
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-xs font-medium uppercase text-slate-500 border-b border-slate-100">
                <tr>
                  <th
                    className="px-5 py-3 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-1.5">
                      <Clock size={13} />
                      Timestamp
                      {sortField === 'created_at' && (
                        sortDirection === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-5 py-3 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('actor_name')}
                  >
                    <div className="flex items-center gap-1.5">
                      <User size={13} />
                      Actor
                      {sortField === 'actor_name' && (
                        sortDirection === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-5 py-3 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('action')}
                  >
                    <div className="flex items-center gap-1.5">
                      <FileText size={13} />
                      Action
                      {sortField === 'action' && (
                        sortDirection === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />
                      )}
                    </div>
                  </th>
                  <th className="px-5 py-3">Entity</th>
                  <th className="px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-12 text-center">
                      <Shield className="text-slate-300 mb-3" size={36} />
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">No audit logs found</h3>
                      <p className="text-sm text-slate-500">
                        {searchTerm || filterAction || filterEntityType || filterStartDate || filterEndDate
                          ? 'Try adjusting your search criteria'
                          : 'No audit logs available'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => (
                    <React.Fragment key={log.id}>
                      <tr className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3 whitespace-nowrap text-left text-sm text-slate-900">
                          {formatTimestamp(log.created_at)}
                        </td>
                        <td className="px-5 py-3 text-left">
                          <div>
                            <p className="text-sm font-medium text-slate-900">{log.actor_name}</p>
                            <p className="text-xs text-slate-500">{log.actor_email}</p>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-left">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-full ${getActionColor(log.action)}`}>
                            {getActionIcon(log.action)}
                            {log.action}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-left">
                          <div>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${getEntityTypeColor(log.entity_type)}`}>
                              {log.entity_type}
                            </span>
                            <p className="text-sm text-slate-900 mt-1">{log.entity_name}</p>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-left">
                          <button
                            onClick={() => setSelectedLog(log)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium inline-flex items-center gap-1"
                          >
                            <Eye size={14} />
                            Details
                          </button>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Details Modal */}
        {selectedLog && (
          <div className="fixed inset-0 bg-slate-900/50 overflow-y-auto h-full w-full z-50">
            <div className="relative min-h-full flex items-center justify-center p-4">
              <div className="bg-white rounded-3xl border border-slate-100 max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-auto ml-64 custom-scrollbar">
                <div className="bg-slate-900 px-6 py-4 sticky top-0 z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Shield className="text-white" size={20} />
                      <div>
                        <h3 className="text-base font-bold text-white">Audit Log Details</h3>
                        <p className="text-slate-400 text-xs mt-0.5">Log ID: {selectedLog.id}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedLog(null)}
                      className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <X className="text-white" size={20} />
                    </button>
                  </div>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)] custom-scrollbar">
                  <div className="space-y-6">
                    {/* Basic Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Clock className="text-slate-400" size={14} />
                          <span className="text-xs font-medium text-slate-500">Timestamp</span>
                        </div>
                        <p className="text-sm font-medium text-slate-900">{formatTimestamp(selectedLog.created_at)}</p>
                      </div>

                      <div className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <User className="text-slate-400" size={14} />
                          <span className="text-xs font-medium text-slate-500">Actor</span>
                        </div>
                        <p className="text-sm font-medium text-slate-900">{selectedLog.actor_name}</p>
                        <p className="text-xs text-slate-500">{selectedLog.actor_email}</p>
                      </div>

                      <div className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <FileText className="text-slate-400" size={14} />
                          <span className="text-xs font-medium text-slate-500">Action</span>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-full ${getActionColor(selectedLog.action)}`}>
                          {getActionIcon(selectedLog.action)}
                          {selectedLog.action}
                        </span>
                      </div>

                      <div className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Tag className="text-slate-400" size={14} />
                          <span className="text-xs font-medium text-slate-500">Entity</span>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${getEntityTypeColor(selectedLog.entity_type)}`}>
                          {selectedLog.entity_type}
                        </span>
                        <p className="text-sm font-medium text-slate-900 mt-1">{selectedLog.entity_name}</p>
                      </div>
                    </div>

                    {/* Technical Details */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-slate-900 mb-3">Technical Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <span className="text-xs text-slate-500">Actor ID</span>
                          <p className="text-sm font-mono text-slate-900 mt-0.5">{selectedLog.actor_id}</p>
                        </div>
                        <div>
                          <span className="text-xs text-slate-500">Entity ID</span>
                          <p className="text-sm font-mono text-slate-900 mt-0.5">{selectedLog.entity_id}</p>
                        </div>
                        <div>
                          <span className="text-xs text-slate-500">IP Address</span>
                          <p className="text-sm font-mono text-slate-900 mt-0.5">{selectedLog.ip_address || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-slate-500">User Agent</span>
                          <p className="text-xs font-mono text-slate-900 mt-0.5 break-all">{selectedLog.user_agent || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Action Details */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-slate-900 mb-2">Action Details</h3>
                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <pre className="text-xs text-slate-900 whitespace-pre-wrap font-mono overflow-x-auto custom-scrollbar">
                          {typeof selectedLog.details === 'object'
                            ? JSON.stringify(selectedLog.details, null, 2)
                            : String(selectedLog.details || 'No details available')}
                        </pre>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 px-6 py-3 flex justify-end border-t border-slate-200">
                  <button
                    onClick={() => setSelectedLog(null)}
                    className="px-4 py-2.5 bg-slate-600 text-white font-medium text-sm rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white rounded-3xl border border-slate-100 px-5 py-4 mt-4">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
              <div className="text-sm text-slate-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
              </div>

              <div className="flex items-center gap-4">
                {/* Page size selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Show:</span>
                  <select
                    value={pagination.limit}
                    onChange={(e) => {
                      const newLimit = parseInt(e.target.value);
                      setPagination(prev => ({
                        ...prev,
                        limit: newLimit,
                        page: 1
                      }));
                    }}
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                {/* Page navigation */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
                    disabled={pagination.page <= 1}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="First page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page <= 1}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-1 mx-1">
                    {(() => {
                      const pages = [];
                      const totalPages = pagination.pages;
                      const currentPage = pagination.page;
                      const maxVisible = 5;

                      let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                      let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                      if (endPage - startPage + 1 < maxVisible) {
                        startPage = Math.max(1, endPage - maxVisible + 1);
                      }

                      if (startPage > 1) {
                        pages.push(
                          <button
                            key={1}
                            onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
                            className="px-2.5 py-1.5 text-sm rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
                          >
                            1
                          </button>
                        );
                        if (startPage > 2) {
                          pages.push(
                            <span key="start-ellipsis" className="px-1.5 text-slate-400 text-sm">...</span>
                          );
                        }
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => setPagination(prev => ({ ...prev, page: i }))}
                            className={`px-2.5 py-1.5 text-sm rounded-lg transition-colors ${
                              i === currentPage
                                ? 'bg-blue-600 text-white'
                                : 'hover:bg-slate-100 text-slate-600'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }

                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(
                            <span key="end-ellipsis" className="px-1.5 text-slate-400 text-sm">...</span>
                          );
                        }
                        pages.push(
                          <button
                            key={totalPages}
                            onClick={() => setPagination(prev => ({ ...prev, page: totalPages }))}
                            className="px-2.5 py-1.5 text-sm rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
                          >
                            {totalPages}
                          </button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page >= pagination.pages}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: pagination.pages }))}
                    disabled={pagination.page >= pagination.pages}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Last page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Jump to page */}
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-slate-500">Go to:</span>
                  <input
                    type="number"
                    min="1"
                    max={pagination.pages}
                    value={pagination.page}
                    onChange={(e) => {
                      const page = parseInt(e.target.value);
                      if (page >= 1 && page <= pagination.pages) {
                        setPagination(prev => ({ ...prev, page }));
                      }
                    }}
                    className="w-14 text-sm border border-slate-200 rounded-lg px-2 py-1.5 text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                    title="Enter page number"
                  />
                  <span className="text-sm text-slate-500">of {pagination.pages}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
